/**
 * MarginBody — the data-bound Margin surface (CD-7: fetch here, kit renders from props).
 * ONE useResolve over the window (site=all — the site slice comes from scope), pivoted
 * into the real Sage P&L ladder (Revenue → −COGS → Gross Profit → −OPEX → EBITDA), the
 * value+margin trend, and the Group→Entity reconciliation. Every number is a served Cell;
 * loading/error/empty states are honest, never a blank or faked panel.
 *
 * ⚠ ONE PERIOD ON THE PAGE (20 Aug 2026). The ladder and the reconciliation used to read a
 * single anchor month while the scope bar named a year, so the page reported two periods at
 * once. Both now read the whole scoped window (viewmodels/margin: windowSummary), and the
 * ladder names any month Sage has not closed rather than letting it pass as a full one. The
 * hero tiles are gone with the anchor: the margin analysis panel carries the header cards
 * for both of its tabs, so the page states its totals once.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from '../components/InfoBanner'
import SageRevenueMargin from '../components/kit/charts/SageRevenueMargin'
import { formatCellValue, periodHeading, EM_DASH } from '../components/kit/cellFormat'
import { formatCurrency } from '../utils/formatters'
import { useMetrics } from '../hooks/useMetrics'
import { useResolve } from '../hooks/useResolve'
import { useMarginTimeline } from '../hooks/useMargin'
import { type Scope } from '../hooks/useScope'
import type { Cell, ResolveResponse, Site } from '../types/metrics'
import { cellLookup, pick, windowSummary, PNL_LADDER, WINDOW_PERIOD, type WindowSummary } from '../viewmodels/margin'
import { monthOrNull, servedSpan } from '../viewmodels/marginFact'
import { intercompany } from '../viewmodels/marginCoverage'
import { sageWindow } from '../viewmodels/marginSage'
import MarginComposition from './MarginComposition'
import MarginBridge, { ERP_OF } from '../components/MarginBridge'
import MarginExplorer from '../components/MarginExplorer'
import MarginGuide from '../components/MarginGuide'

const CARD = 'border border-line rounded-xl bg-panel overflow-hidden'
const HEAD = 'flex items-center gap-2.5 px-4 py-3 border-b border-line2 font-semibold text-[13.5px] text-ink'
/** The §5 numeric rule, on the raw pounds these cells carry. */
const TIE_TOLERANCE = 0.005

/**
 * Revenue − COGS = Gross Profit is a derivation the registry ASSERTS, so the ladder proves it
 * per month rather than assuming it. A month that does not tie is a served-cell defect and
 * gets named: a silent gap makes our arithmetic look wrong instead of the source.
 *
 * ⚠ THIS IS NOT HYPOTHETICAL. Oracle 2026-01 serves Revenue £183,000.00 and COGS £53,000.00
 * against a Gross Profit of £99,449.26 — a £30,550.74 hole. The single-month view never
 * showed January, so nothing on the page could see it.
 *
 * TRACED, 20 Aug 2026. Gross Profit is the leg that is RIGHT. dw.KPI_Monthly_Historical
 * froze that month from two populations: Gross Profit and Depreciation from Sage, and
 * Revenue, COGS, OPEX and EBITDA from the SharePoint board pack, rounded to £1,000.
 * The Sage pair is Revenue £186,511.38 and COGS £87,062.12, which reproduce the served
 * £99,449.26 exactly. The GPM arbitrates nothing — 0.711 is the board pack's own margin
 * (£183,000 − £52,800) / £183,000 rounded to 3 dp, so it belongs to the wrong population
 * too. Fix SQL, and the lock-proc guard that stops it recurring:
 * app/specs/warehouse-fixes/2026-08-20-kpi-monthly-historical-gross-profit.sql
 * It is not applied — escalation item 5 blocks writes to kdxbidata until S6 lands.
 */
function bridgeGap(data: ResolveResponse, site: Site, months: string[]): { residual: number; months: string[] } {
  const lookup = cellLookup(data)
  const gapAt = (period: string): number | null => {
    const [rev, cogs, gp] = ['revenue', 'cogs', 'gross_profit'].map((id) => pick(lookup, id, site, period)?.value ?? null)
    return rev === null || cogs === null || gp === null ? null : rev - cogs - gp
  }
  // ⚠ ONLY THE MONTHS IN THE TOTAL. An OPEN month fails this test by construction — its
  // revenue is a forecast and its cost is actual-to-date — so testing one would report a
  // break in a figure the ladder does not show. It is excluded, and the card title's span
  // is what says so — see WindowNote.
  const untied = months.filter((period) => Math.abs(gapAt(period) ?? 0) >= TIE_TOLERANCE)
  return { residual: untied.reduce((total, period) => total + (gapAt(period) as number), 0), months: untied }
}

/** What the window is made of, and the one thing that can be WRONG with it.
 *
 * ⭐ THE OPEN-MONTH PARAGRAPH IS GONE (24 Aug 2026), and only the paragraph. The exclusion
 * itself is unchanged and still happens in viewmodels/margin.aggregateWindow. It listed the
 * months held out — "Sage has not closed Aug 26, Sept 26, Oct 26, Nov 26, Dec 26" — which is
 * five months of noise a reader already has: the card TITLE names the span it covers
 * (Jan 26 → Jul 26) and the line below counts it. The note could only ever appear when the
 * scope reached into the future, which is exactly when the title already said so.
 *
 * ⚠ The RULE still has to be written down somewhere, and MarginGuide is where — its Lenses
 * section said an unclosed month "stays in the total", the opposite of what the code does,
 * so removing this without fixing that would have left the wrong statement as the only one. */
function WindowNote({ summary, gap }: { summary: WindowSummary; gap: ReturnType<typeof bridgeGap> }): React.JSX.Element {
  const untied = gap.months.map(periodHeading)
  return (
    <div className="px-4 py-2.5 border-t border-line2 font-mono text-[10px] text-mut2 leading-relaxed">
      <div>{summary.months.length} closed months summed · ratios derived from the summed parts, never averaged</div>
      {untied.length > 0 && (
        <div className="text-bad">
          DOES NOT TIE — Revenue − COGS is {formatCurrency(gap.residual, '£', 2)} more than the served Gross Profit.
          The whole gap sits in {untied.join(', ')}. The served cells disagree, not this sum.
        </div>
      )}
    </div>
  )
}

function LadderRow({ cell, label, kind }: { cell: Cell | null; label: string; kind: 'total' | 'less' }): React.JSX.Element {
  const value = cell && cell.value !== null ? formatCellValue(cell) : EM_DASH
  const total = kind === 'total'
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 ${total ? 'bg-panel2' : ''} border-b border-line2`}>
      <span className={`text-[13px] ${total ? 'font-bold text-ink' : 'text-mut'}`}>
        {kind === 'less' ? '−' : '='} {label}
        {cell?.dq_flags.length ? <span className="ml-2 font-mono text-[9px] text-bad">KNOWN-WRONG</span> : null}
      </span>
      <span className={`tabular-nums text-[14px] ${total ? 'font-bold text-ink' : 'text-mut'}`}>{value}</span>
    </div>
  )
}

function PnlLadder({ summary, site, gap }: {
  summary: WindowSummary
  site: Site
  gap: ReturnType<typeof bridgeGap>
}): React.JSX.Element {
  const gpm = pick(summary.lookup, 'gpm', site, WINDOW_PERIOD)
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Sage P&amp;L bridge · {site} · {summary.label}
        <span className="ml-auto font-mono text-[11px] text-mut2">
          GPM {gpm && gpm.value !== null ? formatCellValue(gpm) : EM_DASH}
        </span>
      </div>
      <div>
        {PNL_LADDER.map((row) => (
          <LadderRow key={row.id} cell={pick(summary.lookup, row.id, site, WINDOW_PERIOD)} label={row.label} kind={row.kind} />
        ))}
      </div>
      <WindowNote summary={summary} gap={gap} />
    </div>
  )
}

/** The lifted Margin Analysis surface — the invoice-line drill as a first-class, full-width
 * surface (breadcrumb-led; chart · cards · table descend together).
 *
 * IT READS THE INVOICE FACT NOW (main.fct_margin_line), not the order book filtered to its
 * delivered half. That is what lets it price a line at what was BILLED instead of at list,
 * cost it from the JOB THAT MADE IT instead of a part average with no period in it, carry
 * home currency instead of no FX rate at all, and periodise on the invoice date — which is
 * what reconciles to Sage (revenue +0.1%, COGS −1.3%, GM 31.4% vs 30.7% for 2026 Jan–Jul).
 *
 * The Sage P&L ladder below stays the served scorecard cells: this surface is line-grain
 * margin, that one is the finance statement, and they answer to each other rather than being
 * the same number twice. Both now cover the SAME window, so the two are comparable. */
function MarginAnalysis({ site, periodFrom, periodTo, summary, timeline }: {
  site: Site
  periodFrom: string
  periodTo: string
  summary: WindowSummary
  timeline: ReturnType<typeof useMarginTimeline>
}): React.JSX.Element {
  // ⚠ COMPARABLE ONLY IF THE TWO WINDOWS ARE THE SAME ONE. The cards cover closed INVOICE
  // months and `summary` covers closed LEDGER months; both apply the same closed rule but
  // to different facts, so they can diverge. Comparing across a mismatch would manufacture a
  // variance out of the calendar, so the spans are checked rather than assumed.
  const comparable = summary.label !== '' && summary.label === servedSpan(
    monthOrNull(periodFrom), monthOrNull(periodTo), timeline.data?.open_months_excluded ?? []
  )
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Margin analysis · invoiced work
        <span className="ml-auto font-mono text-[10px] text-mut2 font-normal">billed lines · cut by customer, part or invoice month · every level reconciles</span>
      </div>
      <MarginExplorer
        site={site}
        periodFrom={periodFrom}
        periodTo={periodTo}
        sage={sageWindow(summary.lookup, site)}
        sageComparable={comparable}
      />
    </div>
  )
}

/** The entity split, at Group and nowhere else. At an entity it put the site you are standing
 * on into one of three columns and decomposed a number the page had stopped reporting.
 *
 * ⚠ EVERY ROW BADGES "reconciles", AND THAT IS EXACTLY THE PROBLEM (Mike, 24 Aug 2026). The
 * chips prove Group = Kirkstall + Oracle on the served cells, which is true and is why the
 * trade between the two sites is inside Revenue twice. Sage sums the pair the same way and
 * eliminates nothing. `trade` is the SAME served figure the card strip states — one source,
 * so the two statements can never drift apart. */
function EntitySplit({ summary, trade }: {
  summary: WindowSummary
  trade: ReturnType<typeof intercompany>
}): React.JSX.Element {
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Group → Entity · what makes the number up
        <span className="ml-auto font-mono text-[10px] text-mut2 font-normal">Group = Kirkstall + Oracle · served cells</span>
      </div>
      <div className="cx-scroll overflow-x-auto"><MarginComposition lookup={summary.lookup} period={WINDOW_PERIOD} label={summary.label} /></div>
      <div className="space-y-1.5 px-4 py-2.5 border-t border-line2 text-[11.5px] text-mut2">
        {trade && (
          <p>
            <span className="font-semibold text-amber">Every row here reconciles, and that is the problem.</span>{' '}
            Group is Kirkstall + Oracle with nothing taken out, so the {formatCurrency(trade.value, '£', 0)} one site
            sold to the other sits in Revenue twice. Sage sums these two the same way. The card strip above states
            the amount.
          </p>
        )}
        <p>
          Below entity, Sage serves account-group balances — there is no GL-line grain that reconciles to these cells.
          Per-line margin follows a validated cost source (Kirkstall job cost · Oracle quote at the direct rate).
        </p>
      </div>
    </div>
  )
}

/** The bottom panels. EVERY scope asks the ledger question; only Group asks the second one.
 *
 * ⭐ GROUP CARRIED LESS THAN ITS CHILDREN until 24 Aug 2026 (Mike). The scope picked ONE panel:
 * at an entity, "does the ledger agree", month by month; at Group, "what makes this number up",
 * the whole period as a single column of five totals. So the parent reported the period once
 * while both children reported it seven times, and the Group reader lost the month a break
 * lives in. The month table now draws at every scope, and the entity split follows it at Group
 * rather than standing in for it.
 *
 * Both read the SAME window as the ladder above, so the page states one period. */
function LedgerPanels({ summary, site, timeline }: {
  summary: WindowSummary
  site: Site
  timeline: ReturnType<typeof useMarginTimeline>
}): React.JSX.Element {
  return (
    <>
      <div className={CARD}>
        <div className={HEAD}>
          Sage → ERP · does the ledger agree, and where does it not?
          <span className="ml-auto font-mono text-[10px] text-mut2 font-normal">{site} · by invoice month · the Sage ledger against {ERP_OF[site] ?? 'the ERP'}</span>
        </div>
        <MarginBridge lookup={summary.lookup} months={summary.months} site={site} query={timeline} windowLabel={summary.label} />
      </div>
      {site === 'Group' && (
        <EntitySplit summary={summary} trade={intercompany(timeline.data?.total ?? null, site)} />
      )}
    </>
  )
}

export default function MarginBody({ scope }: { scope: Scope }): React.JSX.Element {
  const catalogue = useMetrics()
  // The SAME query key MarginExplorer's trend uses, so this is a cache read and not a second
  // request — and the two panels can never end up reporting different months.
  const timeline = useMarginTimeline(monthOrNull(scope.periodFrom), monthOrNull(scope.periodTo), scope.site)
  // Sage keeps months, so this surface reads monthly whatever the URL carries — the page
  // no longer offers a grain axis, and an old ?cadence=weekly link must not empty the page.
  const resolve = useResolve(scope.periodFrom, scope.periodTo, 'monthly', 'all')
  const error = resolve.error ?? catalogue.error
  if (error) return <InfoBanner variant="error" icon={AlertCircle} title="Margin unavailable" message={error.message} />
  if (!catalogue.data || !resolve.data) {
    return <div role="status" className="animate-pulse rounded-xl border border-line bg-panel px-4 py-12 text-center text-sm text-mut2">Loading margin…</div>
  }
  const data = resolve.data
  const summary = windowSummary(data, scope.site)
  if (summary.months.length === 0) {
    return <InfoBanner variant="info" message={`No finance cells served for ${scope.site} in ${scope.periodFrom} → ${scope.periodTo}.`} />
  }
  return (
    <div className="space-y-4">
      <MarginGuide site={scope.site} />
      <MarginAnalysis site={scope.site} periodFrom={scope.periodFrom} periodTo={scope.periodTo} summary={summary} timeline={timeline} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
        <PnlLadder summary={summary} site={scope.site} gap={bridgeGap(data, scope.site, summary.months)} />
        <SageRevenueMargin data={data} site={scope.site} />
      </div>
      <LedgerPanels summary={summary} site={scope.site} timeline={timeline} />
    </div>
  )
}
